import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getLast12MonthsText } from './BankStatements';
import styles from './UploadStatementFlow.module.scss'
import MeritoButton from '../../../components/common/MeritoButtons/MeritoButtons';
import API from '../../../rest/api';
import { RiErrorWarningFill as ErrorIcon } from 'react-icons/ri';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { checkPDFProtection } from '../components/utils/functions';
import { ApplicationContextType, MonthlyReportSteps, pdfProtectionStates, StatementAnalysisErrorTypes, StatementAnalysisStatus, StatementStepType, UnderwritingSteps, uploadStates } from '../../../interfaces/interfaces';
import { UnderwritingFormContext } from '../context/UnderwritingFormContext';
import { randomUUID } from 'crypto';
import { getIn } from 'final-form';
import { Field, useForm } from 'react-final-form';
import { delay } from 'lodash';
import { FieldArray } from 'react-final-form-arrays';
import { VscFilePdf as PdfIcon } from 'react-icons/vsc';
import CircularLoader from '../../../components/common/CircularLoader/CircularLoader';
import { RiCloseFill as RemoveIcon } from 'react-icons/ri'
import moment from 'moment';
import FileItem from '../components/MultiFileUploadModal/FileItem';
import { MonthlyReportFormContext } from '../../MonthlyReport/context/MonthlyReportFormContext';
import MeritoCheckbox from '../../../components/common/MeritoCheckbox/MeritoCheckbox';
import { RestS } from '../../../rest/rest-service';

type Props = {
    fileSizeLimit?: number;
    documentType?: string;
    fieldName: string;
    newUploadedDocIds: number[];
    setNewUploadedDocIds: React.Dispatch<React.SetStateAction<number[]>>;
    deleteDocIds: number[],
    setDeleteDocIds: React.Dispatch<React.SetStateAction<number[]>>;
    isJobProcessing: boolean;
    currentAnalysisJob: { [x: string]: any };
    contextType?: ApplicationContextType;
    stepType?: UnderwritingSteps | MonthlyReportSteps;
    isOldAccount: boolean;
}


const UploadStatementFlow = ({ fileSizeLimit = 30, isOldAccount, documentType = "bankStatement", fieldName, newUploadedDocIds, setNewUploadedDocIds, deleteDocIds, setDeleteDocIds, isJobProcessing, currentAnalysisJob, contextType = ApplicationContextType.underwriting, stepType = UnderwritingSteps.documents }: Props) => {
    // const [files, setFiles] = useState([]);
    const fileInputRef = useRef(null);
    const [invalidFileError, setInvalidFileError] = useState(null);
    const { handleUnderwritingFileJobs, underwritingFileJobs, formData, sendFileJobToApi, fetchStatementAnalysisJobs } = useContext(contextType == ApplicationContextType.monthlyReport ? MonthlyReportFormContext : UnderwritingFormContext);
    const [isOverridingError, setIsOverridingError] = useState(false);
    const [isRevertingError, setIsRevertingError] = useState(false);

    const { getState, change } = useForm();





    const [latestUnderwritingFileJobs, setLatestUnderwritingFileJobs] = useState(underwritingFileJobs);

    useEffect(() => {
        // console.log('Updated underwritingFileJobs:', underwritingFileJobs);
        setLatestUnderwritingFileJobs(underwritingFileJobs);  // Update state to trigger re-renders
    }, [underwritingFileJobs]);




    const statementJobId = getIn(getState()?.values, `${fieldName}.statementJobId`);
    const existingStatements = getIn(getState()?.values, `${fieldName}.statements`);
    const statementType = getIn(getState()?.values, `${fieldName}.statementType`);

    // console.log('existingStatements', existingStatements, statementJobId);

    const validateFile = (file: File | undefined) => {
        const allowedFormats = ['application/pdf'];
        const maxFileSize = fileSizeLimit * 1024 * 1024;

        if (!file) {
            return 'Upload document'
        }

        if (!allowedFormats.includes(file.type)) {
            return 'Only PDF files are accepted'
        }
        if (file.size > maxFileSize) {
            return `File size should be less than ${fileSizeLimit} MB`
        }
        return null;
    }

    const uploadFileToS3 = async (fieldName: string, fileData: File, fileFormJob: any, initialJob: any) => {
        try {
            const formData = new FormData();
            formData.append('documentType', documentType);
            formData.append('file', fileData);
            const data = await API.post('/app/uploadDocs', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            }).then((res) => res.data);
            const isPdfProtected = await checkPDFProtection(fileData);
            console.log('fieldName', `${stepType}.${fieldName}`);
            console.log('isPdfProtected', isPdfProtected);
            let jobDetails = null
            if (isPdfProtected == pdfProtectionStates?.passwordNotRequired) {
                // handleUnderwritingFileJobs(UnderwritingSteps.documents, )
                jobDetails = await sendFileJobToApi(stepType, fieldName, {
                    ...initialJob,
                    s3FileKey: data?.Key,
                    fileMetaData: {
                        fileName: fileData?.name,
                        fileType: fileData?.type,
                        requiredPassword: false,
                        password: null,
                    },
                    status: uploadStates.doneUploading,
                })
            } else if (isPdfProtected == pdfProtectionStates.passwordRequired) {
                jobDetails = await sendFileJobToApi(stepType, fieldName, {
                    ...initialJob,
                    s3FileKey: data?.Key,
                    fileMetaData: {
                        fileName: fileData?.name,
                        fileType: fileData?.type,
                        requiredPassword: true,
                        password: null,
                    },
                    status: uploadStates.requiredPassword,
                })
            }

            setNewUploadedDocIds((prevDocIds) => [...prevDocIds, jobDetails?.id]);
            change(`${fieldName}`, {
                ...fileFormJob,
                uploadedFile: {
                    fileKey: data?.Key,
                    fileName: fileData?.name,
                    fileType: fileData?.type,
                    docId: jobDetails?.id,
                }
            });
        } catch (error) {
            console.log('error', error);
        } finally {
            //   setFileUploadLoading(false);
            //   handleUploadStatus(uploadStates.doneUploading);
        }
    }

    // console.log('statementJobId', statementJobId);


    const handleIndividualFiles = async (file: File, fieldName: string, fileFormJob: any) => {
        // individual files handling
        const initialJob = {
            jobId: statementJobId,
            // formId: 
            step: stepType,
            section: StatementStepType.pdf_upload,
            formFieldName: fieldName,
            s3FileKey: null,
            fileMetaData: {
                fileName: file?.name,
                fileType: file?.type,
                requiredPassword: false,
                password: null,
            },
            status: uploadStates.uploading
        };
        handleUnderwritingFileJobs(stepType, fieldName, initialJob)
        uploadFileToS3(fieldName, file, fileFormJob, initialJob);
    }



    const handleFileChange = (event) => {
        const newFiles: File[] = Array.from(event.target.files);
        // setFiles((prevFiles) => [...prevFiles, ...newFiles]);
        // const errorFiles = []
        let fileError = null;
        newFiles.forEach((file) => {
            const error = validateFile(file);
            if (error) {
                // errorFiles.push(file?.name)
                fileError = error;
            }
        });
        if (fileError) {
            setInvalidFileError({
                title: "The Uploaded files(s) is invalid",
                subTitle: fileError,
            });
            return;
        } else {
            setInvalidFileError(null);
        }
        if (newFiles.length && statementType == StatementStepType.net_banking) {
            change(`${fieldName}.statementType`, StatementStepType.pdf_upload);
        }
        // const existingFiles = existingStatements;
        // const updatedFiles = [...existingFiles];
        const olderStatementsLength = existingStatements?.length ?? 0;
        console.log('olderStatementsLength', olderStatementsLength);

        console.log('get state', getState()?.values)
        newFiles.forEach((file, index) => {
            const currentIndex = olderStatementsLength + index;
            const currentFileFieldName = `${fieldName}.statements[${currentIndex}]`;
            console.log('currentFileFieldName', currentFileFieldName);
            const fileFormJob = {
                selectedFile: file,
                uploadedFile: null,
            }
            change(`${currentFileFieldName}`, fileFormJob)
            handleIndividualFiles(file, currentFileFieldName, fileFormJob);
        });
    };




    const showAnalysisJobError = useMemo(() => {
        if (currentAnalysisJob?.status == StatementAnalysisStatus.failure) {
            // let errorDetails = null;
            console.log('currentAnalysisJob?.errorDetails', currentAnalysisJob?.errorDetails);
            if (!currentAnalysisJob?.errorDetails?.length && !currentAnalysisJob?.errorDetails?.errors?.length) {
                // base state invalid statement
                return {
                    title: 'The uploaded file(s) is invalid or cannot be processed.',
                    subTitle: 'Download the latest statement(s) again and re-upload.',
                    missingPeriods: []
                }
            } else {
                const errorObj = currentAnalysisJob?.errorDetails;
                const errorArr = errorObj?.errors?.length ? new Set(errorObj?.errors) : new Set();
                if (!errorArr.size) return null;
                if (errorArr.has(StatementAnalysisErrorTypes.EOD_MISSING_RANGES)) {
                    const formattedPeriods = errorObj?.errorResults?.eod_balance_missing_ranges?.reduce((prev, curVal) => {
                        const startDate = moment(curVal[0], "YYYY-MM-DD");
                        const endDate = moment(curVal[1], "YYYY-MM-DD");
                        const isSameDate = startDate.isSame(endDate, 'day');
                        prev.push({
                            formattedStartDate: startDate.isSame(endDate, 'year') ? startDate.format('MMM DD') : startDate.format('MMM DD, YYYY'),
                            formattedEndDate: endDate.format('MMM DD, YYYY'),
                            isSameDate,
                        })
                        return prev;
                    }, [])
                    return {
                        title: "Transactions are missing for these periods.",
                        subTitle: "Upload statements for these periods or confirm below that there were none.",
                        missingPeriods: formattedPeriods
                    }
                    /* The lambda no longer adds BALANCE_RECONCILIATION_FAILED to errors
                    (it's tracked only via errorResults.balance_reconciliation for DB/Slack),
                    so this branch is unreachable. Left commented for reference / rollback.
                    } else if (errorArr.has(StatementAnalysisErrorTypes.BALANCE_RECONCILIATION_FAILED)) {
                    return {
                        title: "Reconciliation failed.",
                        subTitle: <>Transactions do not match recorded data. Download the statements again and reupload. <br />If the issue persists, contact Merito.</>,
                        missingPeriods: []
                    } */
                } else {
                    return {
                        title: 'The uploaded file(s) is invalid or cannot be processed.',
                        subTitle: 'Download the latest statement(s) again and re-upload.',
                        missingPeriods: []
                    }
                }
            }
        }
    }, [currentAnalysisJob]);

    const handleConfirmNoTransactions = async (checked: "yes" | "no" | "partial") => {
        if (checked !== "yes" || isOverridingError || !currentAnalysisJob?.id) return;
        try {
            setIsOverridingError(true);
            await RestS.overrideStatementAnalysisError(currentAnalysisJob.id);
            fetchStatementAnalysisJobs();
        } catch (error) {
            console.log('error', error);
        } finally {
            setIsOverridingError(false);
        }
    };

    const confirmedMissingPeriodsCount = useMemo(() => {
        if (currentAnalysisJob?.status !== StatementAnalysisStatus.errorOverrided) return 0;
        return currentAnalysisJob?.errorDetails?.errorResults?.eod_balance_missing_ranges?.length ?? 0;
    }, [currentAnalysisJob]);

    const handleUndoConfirmation = async () => {
        if (isRevertingError || !currentAnalysisJob?.id) return;
        try {
            setIsRevertingError(true);
            await RestS.revertStatementAnalysisError(currentAnalysisJob.id);
            fetchStatementAnalysisJobs();
        } catch (error) {
            console.log('error', error);
        } finally {
            setIsRevertingError(false);
        }
    };

    return (
        <div id={`${fieldName}.statements`} className={styles.uploadStatementFlowRootContainer}>
            <div id={`${fieldName}.statementJobId`} className={styles.textContainer}>
                <div className={styles.title}>
                    Upload Bank Statements
                </div>
                <div className={styles.subTitle}>
                    {isOldAccount && contextType == ApplicationContextType.monthlyReport ?
                        <>
                            For the <b>last month</b> ({`${moment().subtract(1, 'month').format('MMM YYYY')}`}).
                        </>
                        :
                        <>
                            Upload statements for the <span>last 12 months</span> ({`${getLast12MonthsText()?.formattedText}`}).
                        </>}
                </div>
            </div>
            <div className={styles.uploadContainer}>
                <input
                    type="file"
                    multiple
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    style={{ display: "none" }}
                    accept='.pdf'
                />

                {/* Button triggers file input click */}
                <div className={styles.btnRootContainer}>
                    <MeritoButton disabled={isJobProcessing} type='primary-1' size='md2' onClick={() => {
                        if (isJobProcessing) return;
                        fileInputRef.current.click();
                    }} btnText="Upload Statements" />
                </div>
                {isJobProcessing ?
                    <div className={styles.processingContainer}>
                        <div className={styles.loader}>
                            <CircularLoader width='1.25rem' height='1.25rem' />
                        </div>
                        <div className={styles.text}>
                            Processing your files. Please wait...
                        </div>
                    </div>

                    : <div className={styles.subText}>
                        Accepted file type: PDF. You can upload multiple files.
                    </div>}
            </div>

            {confirmedMissingPeriodsCount ?
                <div className={styles.confirmedBannerContainer}>
                    <div className={styles.leftContainer}>
                        <div className={styles.iconContainer}>
                            <InfoIcon />
                        </div>
                        <div className={styles.text}>
                            <span className={styles.textBold}>{confirmedMissingPeriodsCount} period{confirmedMissingPeriodsCount > 1 ? 's' : ''}</span> confirmed with no transactions.
                        </div>
                    </div>
                    <MeritoButton
                        type="secondary-2"
                        size="md"
                        btnType="button"
                        disabled={isRevertingError}
                        loading={isRevertingError}
                        onClick={handleUndoConfirmation}
                        btnText="Undo"
                    />
                </div>
                : showAnalysisJobError ?
                    <div className={styles.errorRootContainer}>
                        <div className={styles.topContainer}>
                            <div className={styles.titleContainer}>
                                <div className={styles.iconContainer}>
                                    <ErrorOutlineIcon />
                                </div>
                                <div className={styles.title}>
                                    {showAnalysisJobError?.title}
                                </div>
                            </div>
                            <div className={styles.subTitle}>
                                {showAnalysisJobError?.subTitle}
                            </div>
                        </div>
                        {showAnalysisJobError?.missingPeriods.length ?
                            <div className={styles.periodsContainer}>
                                <div className={styles.listTitle}>
                                    Missing Periods:
                                </div>
                                <div className={styles.listContainer}>
                                    {showAnalysisJobError?.missingPeriods.map((period, index) => {
                                        return (
                                            <div className={styles.listItem}>
                                                {period.isSameDate ? period.formattedStartDate : `${period.formattedStartDate} - ${period.formattedEndDate}`}
                                            </div>
                                        )
                                    })}
                                </div>
                            </div> : null}
                        {showAnalysisJobError?.missingPeriods.length ?
                            <div className={styles.confirmNoTransactionsContainer}>
                                <MeritoCheckbox
                                    size='md1'
                                    checked={isOverridingError ? "yes" : "no"}
                                    state={isOverridingError ? "disabled" : "default"}
                                    onChange={handleConfirmNoTransactions}
                                    label="I confirm there were no transactions during these periods."
                                />
                            </div> : null}
                    </div> : null}

            <FieldArray name={`${fieldName}.statements`}>
                {({ fields }) => {
                    return (<div className={styles.uploadedFilesContainer}>
                        {fields.map((innerFieldName, fieldIndex) => {
                            return (
                                <Field name={`${innerFieldName}`}>
                                    {(props) => {
                                        return (
                                            <>
                                                <FileItem
                                                    outerFieldName={`${fieldName}.statements`}
                                                    fieldName={innerFieldName}
                                                    fieldIndex={fieldIndex}
                                                    newUploadedDocIds={newUploadedDocIds}
                                                    setNewUploadedDocIds={setNewUploadedDocIds}
                                                    deleteDocIds={deleteDocIds}
                                                    setDeleteDocIds={setDeleteDocIds}
                                                    stepType={stepType}
                                                    isJobProcessing={isJobProcessing}
                                                    contextType={contextType}
                                                />
                                            </>
                                            // <div className={styles.fileItemRootContainer}>
                                            //     <div className={styles.fileItemContainer}>
                                            //         <div className={styles.leftContainer}>
                                            //             <div className={styles.iconContainer}>
                                            //                 <PdfIcon />
                                            //             </div>
                                            //             <div className={styles.fileName}>
                                            //                 {Object.keys(props.input.value?.selectedFile).length ? props.input.value?.selectedFile?.name : props.input.value?.uploadedFile?.fileName}
                                            //             </div>
                                            //         </div>
                                            //         <div className={styles.rightContainer}>
                                            //             {getJobDetails(fieldName)?.status == uploadStates.uploading ?
                                            //                 <CircularLoader />
                                            //                 : <MeritoButton
                                            //                     type='tertiary'
                                            //                     disabled={isJobProcessing}
                                            //                     onlyIcon={true}
                                            //                     icon={<RemoveIcon />}
                                            //                     onClick={() => {
                                            //                         if (isJobProcessing) return;
                                            //                         setDeleteDocIds(prev => [...prev, props.input?.value?.uploadedFile?.docId]);
                                            //                         fields.remove(fieldIndex);
                                            //                     }}
                                            //                 />}
                                            //         </div>
                                            //     </div>

                                            // </div>
                                        )
                                    }}
                                </Field>
                            )
                        })}
                    </div>)
                }}
            </FieldArray>
            {invalidFileError ? <div className={styles.errorContainer}>
                <div className={styles.errorTitleContainer}>
                    <div className={styles.iconContainer}>
                        <ErrorIcon />
                    </div>
                    <div className={styles.text}>{invalidFileError.title}</div>
                </div>
                <div className={styles.errorSubTitle}>
                    {invalidFileError.subTitle}
                </div>
            </div> : null}
        </div>
    );
}

export default UploadStatementFlow